import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

const SelectContext = React.createContext();

const Select = ({ children, value, onValueChange = () => {}, ...props }) => {
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const selectRef = useRef(null);
  const triggerRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inSelect = selectRef.current?.contains(event.target);
      const inPortal = portalRef.current?.contains(event.target);
      if (!inSelect && !inPortal) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const registerItem = (itemValue, itemLabel) => {
    if (value === itemValue && itemLabel !== selectedLabel) {
      setSelectedLabel(itemLabel);
    }
  };

  return (
    <SelectContext.Provider
      value={{ value, onValueChange, open, setOpen, selectedLabel, registerItem, triggerRef, portalRef }}
    >
      <div className="relative" ref={selectRef} {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

const SelectTrigger = React.forwardRef(({ className = '', children, ...props }, ref) => {
  const context = React.useContext(SelectContext);

  if (!context) {
    console.warn('SelectTrigger must be used within a Select component');
    return null;
  }

  const { open, setOpen, triggerRef } = context;

  return (
    <button
      ref={(node) => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      type="button"
      onClick={() => setOpen(!open)}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    >
      {children}
      <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
  );
});

const MAX_MENU_HEIGHT = 200;

const sanitizeContentClassName = (className = '') =>
  className
    .replace(/\boverflow-[xy]-\S+/g, '')
    .replace(/\bmax-h-\[[^\]]+\]/g, '')
    .replace(/\bmax-h-\S+/g, '')
    .trim();

const SelectContent = React.forwardRef(
  ({ className = '', forceDownward = false, disableScrollbar = false, children, ...props }, ref) => {
    const context = React.useContext(SelectContext);
    const [position, setPosition] = useState('bottom');
    const [menuStyle, setMenuStyle] = useState(null);
    const [listMaxHeight, setListMaxHeight] = useState(MAX_MENU_HEIGHT);

    const updateMenuPosition = useCallback(() => {
      const triggerEl = context?.triggerRef?.current;
      if (!triggerEl) return;

      const rect = triggerEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = !forceDownward && spaceBelow < MAX_MENU_HEIGHT && spaceAbove > spaceBelow;

      setPosition(openUp ? 'top' : 'bottom');
      setListMaxHeight(
        openUp
          ? Math.min(MAX_MENU_HEIGHT, spaceAbove - 8)
          : Math.min(MAX_MENU_HEIGHT, spaceBelow - 8)
      );
      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
        ...(openUp
          ? { bottom: viewportHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }, [context?.triggerRef, forceDownward]);

    useEffect(() => {
      if (!context?.open) {
        setPosition('bottom');
        setMenuStyle(null);
        return undefined;
      }

      updateMenuPosition();

      window.addEventListener('resize', updateMenuPosition);
      window.addEventListener('scroll', updateMenuPosition, true);

      return () => {
        window.removeEventListener('resize', updateMenuPosition);
        window.removeEventListener('scroll', updateMenuPosition, true);
      };
    }, [context?.open, updateMenuPosition]);

    if (!context) {
      console.warn('SelectContent must be used within a Select component');
      return null;
    }

    const { open, portalRef } = context;

    const decorativeClassName = sanitizeContentClassName(className);
    const scrollClasses = disableScrollbar
      ? 'overflow-y-auto overflow-x-hidden'
      : 'overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100';
    const menuClasses = `rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-1 ${scrollClasses} ${decorativeClassName}`;

    if (!open) {
      return (
        <div style={{ display: 'none' }} aria-hidden="true">
          {children}
        </div>
      );
    }

    if (!menuStyle) {
      return null;
    }

    const portalNode = (
      <div
        ref={(node) => {
          portalRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={menuClasses}
        style={{ ...menuStyle, maxHeight: listMaxHeight }}
        data-select-position={position}
        {...props}
      >
        {children}
      </div>
    );

    return createPortal(portalNode, document.body);
  }
);

const SelectItem = React.forwardRef(({ className = '', children, value: itemValue, ...props }, ref) => {
  const context = React.useContext(SelectContext);
  const itemRef = React.useRef(null);

  const value = context?.value;
  const onValueChange = context?.onValueChange;
  const setOpen = context?.setOpen;
  const registerItem = context?.registerItem;
  const isSelected = value === itemValue;

  React.useEffect(() => {
    if (isSelected && registerItem) {
      const label =
        typeof children === 'string' ? children : itemRef.current?.textContent || itemValue;
      registerItem(itemValue, label);
    }
  }, [isSelected, itemValue, children, registerItem]);

  if (!context) {
    console.warn('SelectItem must be used within a Select component');
    return null;
  }

  const handleClick = () => {
    if (onValueChange && typeof onValueChange === 'function') {
      onValueChange(itemValue);
    }
    if (setOpen && typeof setOpen === 'function') {
      setOpen(false);
    }
  };

  return (
    <div
      ref={(node) => {
        itemRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      onClick={handleClick}
      className={`relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 ${
        isSelected
          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-medium'
          : 'text-gray-900 dark:text-gray-100'
      } data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
});

const SelectValue = ({ placeholder, children, ...props }) => {
  const context = React.useContext(SelectContext);

  if (!context) {
    console.warn('SelectValue must be used within a Select component');
    return <span className="text-left flex-1 text-gray-400">{placeholder || 'Select...'}</span>;
  }

  const { value, selectedLabel } = context;

  if (children) {
    return (
      <span className="text-left flex-1" {...props}>
        {children}
      </span>
    );
  }

  if (value && selectedLabel) {
    return (
      <span className="text-left flex-1 truncate" {...props}>
        {selectedLabel}
      </span>
    );
  }

  return (
    <span className="text-left flex-1 text-gray-400" {...props}>
      {placeholder || 'Select...'}
    </span>
  );
};

SelectTrigger.displayName = 'SelectTrigger';
SelectContent.displayName = 'SelectContent';
SelectItem.displayName = 'SelectItem';
SelectValue.displayName = 'SelectValue';

export { Select, SelectTrigger, SelectContent, SelectItem, SelectValue };
